import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const FREE_WATCHLIST_LIMIT = 5;

interface WatchlistContextValue {
  watchlist: string[];
  loading: boolean;
  isInWatchlist: (ticker: string) => boolean;
  toggleWatchlist: (ticker: string) => Promise<'added' | 'removed' | 'limit'>;
  canAddMore: boolean;
  freeLimit: number;
}

const WatchlistContext = createContext<WatchlistContextValue | null>(null);

export const WatchlistProvider = ({ children }: { children: ReactNode }) => {
  const { user, isPro } = useAuth();
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const fetchWatchlist = useCallback(async () => {
    if (!user) {
      setWatchlist([]);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_watchlist')
        .select('ticker')
        .eq('user_id', user.id);
      if (error) throw error;
      setWatchlist((data || []).map((r) => r.ticker));
    } catch (err) {
      console.error('Failed to fetch watchlist:', err);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const isInWatchlist = useCallback(
    (ticker: string) => watchlist.includes(ticker.toUpperCase()),
    [watchlist]
  );

  const canAddMore = isPro || watchlist.length < FREE_WATCHLIST_LIMIT;

  const toggleWatchlist = useCallback(
    async (ticker: string): Promise<'added' | 'removed' | 'limit'> => {
      if (!user) return 'limit';
      const upperTicker = ticker.toUpperCase();

      if (isInWatchlist(upperTicker)) {
        setWatchlist((prev) => prev.filter((t) => t !== upperTicker));
        await supabase
          .from('user_watchlist')
          .delete()
          .eq('user_id', user.id)
          .eq('ticker', upperTicker);
        return 'removed';
      } else {
        if (!isPro && watchlist.length >= FREE_WATCHLIST_LIMIT) {
          return 'limit';
        }
        setWatchlist((prev) => [...prev, upperTicker]);
        await supabase
          .from('user_watchlist')
          .insert({ user_id: user.id, ticker: upperTicker });
        return 'added';
      }
    },
    [user, isInWatchlist, isPro, watchlist.length]
  );

  return (
    <WatchlistContext.Provider
      value={{ watchlist, loading, isInWatchlist, toggleWatchlist, canAddMore, freeLimit: FREE_WATCHLIST_LIMIT }}
    >
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlistContext = () => {
  const ctx = useContext(WatchlistContext);
  if (!ctx) {
    throw new Error('useWatchlistContext must be used within WatchlistProvider');
  }
  return ctx;
};
